import React, { useCallback, useEffect, useState } from 'react';
import block from 'propmods';
import { ThemeProvider, ToasterComponent, ToasterProvider } from '@gravity-ui/uikit';
import { BrowserRouter, Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { Lang } from '@yandex-int/i18n/lib/react';

import { reachGoal } from 'client/lib/metrika';
import { keepIfInEnum } from 'common/utils/keepIfInEnum';
import { createApp, createSkill, getAppStoreSnapshot, getSnapshot } from '../../api';
import { routes } from '../../lib/routes';
import { Channel } from '../../model/skill';
import { AppStoreSnapshot, Snapshot } from '../../types';

import Header from '../Header/Header';
import { Loading } from '../Loading/Loading';
import InstallableAppsIndex from 'client/blocks-redesign/InstallableAppsIndex/InstallableAppsIndex';
import AuthReturnPage from './AuthReturnPage';
import SkillCreationPopup from '../SkillCreationPopup/SkillCreationPopup';
import SkillsIndex from '../SkillsIndex/SkillsIndex';
import ModerationTestPage from './ModerationTestPage';
import UnsubscribePage from './UnsubscribePage';
import EmailConfirmationPage from './EmailConfirmationPage';
import * as SnapshotContext from '../../context/snapshotContext';
import * as AppStoreSnapshotContext from '../../context/appStoreSnapshotContext';
import SkillPageContainer from '../SkillPage/SkillPageContainer';
import InstallableAppPageContainer from '../../blocks-redesign/InstallableAppPage/InstallableAppPageContainer';
import {
    isIframe,
    hrefIsExternal,
    showAppStoreUi,
    getIsYangoRequest,
    showSmartScreenUi,
    showAutoAppUi,
    isInstallableAppChannel,
} from '../../utils';
import { tycoon } from '../../utils/messages';
import SettingsPage from './AppSettingsPage';
import AsyncProgress from '../AsyncProgress/AsyncProgress';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import NewsPreview from '../NewsPreview/NewsPreview';
import Announcement from '../Announcement/Announcement';
import { useInterval } from '../../hooks/useInterval';
import { useSessionFlags } from '../../hooks/useSessionFlags';
import { DocumentationPage } from '../DocumentationPage/DocumentationPage';
import { useMessageSubscription } from '../../hooks/useMessageSubscription';
import { mapChannelToDefaultName } from 'client/utils/settings/alice-skill';
import { ALICE_APP_ROUTE } from 'common/routes';

import '@gravity-ui/uikit/styles/styles.css';

import './App.scss';

const { links, snapshotRefreshIntervalMs, urlRoot } = __appConfig.app;

interface CreatePopup {
    visible?: boolean;
    progress?: boolean;
}

const b = block('App');

const App: React.FC = () => {
    const [theme, setTheme] = useState<'light' | 'dark'>('light');

    useEffect(() => {
        const updateColorScheme = () => {
            if (window.matchMedia) {
                if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
                    setTheme('dark');
                } else {
                    setTheme('light');
                }
            }
        };
        updateColorScheme();
        const colorSchemeQuery = !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
        if (colorSchemeQuery) {
            colorSchemeQuery.addEventListener('change', updateColorScheme);
        }
        return () => {
            if (colorSchemeQuery) {
                colorSchemeQuery.removeEventListener('change', updateColorScheme);
            }
        };
    }, []);

    const location = useLocation();
    const isAutoAppPage = location.pathname.startsWith(`${urlRoot}/${ALICE_APP_ROUTE[Channel.AutoApp]}`);
    const isTvAppPage = location.pathname.startsWith(`${urlRoot}/${ALICE_APP_ROUTE[Channel.TvApp]}`);
    const isSmartScreenPage = location.pathname.startsWith(`${urlRoot}/${ALICE_APP_ROUTE[Channel.SmartScreenApp]}`);

    if (isTvAppPage || isSmartScreenPage || isAutoAppPage) {
        return (
            <ErrorBoundary withHomepageLink>
                <ThemeProvider theme={theme}>
                    <ToasterProvider>
                        <Switch>
                            <Route component={PrivateApp} />
                        </Switch>
                        <ToasterComponent />
                    </ToasterProvider>
                </ThemeProvider>
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary withHomepageLink>
            <ThemeProvider theme={'light'}>
                <div {...b({ 'in-iframe': isIframe() })}>
                    <Header />
                    {!getIsYangoRequest() && <NewsPreview />}
                    <Announcement />
                    <Switch>
                        <Route path={routes.app.email} exact component={EmailConfirmationPage} />
                        <Route path={routes.app.unsubscribe} exact component={UnsubscribePage} />
                        <Route path={routes.app.accountLinkingComplete} exact component={AuthReturnPage} />
                        <Route component={PrivateApp} />
                    </Switch>
                </div>
            </ThemeProvider>
        </ErrorBoundary>
    );
};

const PrivateApp: React.FC = () => {
    const history = useHistory();
    const [snapshot, setSnapshot] = useState<Snapshot | undefined>();
    const [appStoreSnapshot, setAppStoreSnapshot] = useState<AppStoreSnapshot | undefined>();
    const [createPopup, setCreatePopup] = useState<CreatePopup>({});
    const [appCreationInProgress, setAppCreationInProgress] = useState<boolean>(false);
    const user = __appConfig.user;

    useSessionFlags([
        '__display-smart-home-testing-page',
        '__display-cloud-functions',
        '__display-smart-home-cloud-functions',
        '__display-monitoring-page',
        '__display-smart-home-monitoring-page',
        '__display-donation-page',
        '__display-device-linking',
        '__display-app-metrica-settings',
        '__display-rsy-field',
        '__display-trusted-checkbox',
        '__display-anonymous-option',
        '__display-skill-access',
        '__display-sharing-page',
        '__display-testing-date-selector',
    ]);

    const fetchSkills = useCallback(async () => {
        try {
            setSnapshot(await getSnapshot());
        } catch (err) {
            console.error(err);
        }
    }, []);

    const fetchApps = useCallback(async () => {
        if (!showAppStoreUi()) {
            return;
        }

        try {
            const res = await getAppStoreSnapshot();
            setAppStoreSnapshot(res);
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => {
        fetchSkills();

        if (showAppStoreUi()) {
            fetchApps();
        }
    }, [fetchApps, fetchSkills]);

    useInterval(fetchSkills, snapshotRefreshIntervalMs);
    useInterval(fetchApps, snapshotRefreshIntervalMs);

    useMessageSubscription(async (data) => {
        switch (data.type) {
            case 'navigation':
                if (!hrefIsExternal.test(data.payload.path)) {
                    history.push(data.payload.path);
                }
                return;

            case 'sprav_dialogs_inited':
                tycoon.sendIframeInited();

                return;
            default:
                return;
        }
    });

    useEffect(() => {
        tycoon.sendIframeInited();
    }, []);

    const createNewSkill = useCallback(
        async (channelValue: string | undefined) => {
            const channel = keepIfInEnum(channelValue, Channel);
            if (!channel || isInstallableAppChannel(channel)) {
                console.error('@App wrong channel type ', channelValue);
                return;
            }
            const skill =
                channel === Channel.Thereminvox
                    ? await createSkill(channel)
                    : await createApp(mapChannelToDefaultName[channel], channel);
            await fetchSkills();

            history.push(routes.app.skillPage(true, 'settings', skill.id));
            reachGoal('createSkill');
        },
        [fetchSkills, history],
    );

    const handleSkillCreationPopupSelect = useCallback(
        async (channel: Channel) => {
            setCreatePopup((prev) => ({ ...prev, progress: true }));

            await createNewSkill(channel);

            setCreatePopup((prev) => ({ ...prev, visible: false }));
        },
        [setCreatePopup, createNewSkill],
    );

    const handleAppCreation = useCallback(
        async (channel: Channel) => {
            if (appCreationInProgress || !showAppStoreUi()) {
                return;
            }
            if (!isInstallableAppChannel(channel)) {
                console.error('@handleAppCreation wrong channel type');
                return;
            }

            setAppCreationInProgress(true);
            const { skillId } = await createApp(mapChannelToDefaultName[channel], channel);
            await fetchApps();
            history.push(routes.app.installableAppPage(channel, true, 'settings', skillId));
            setAppCreationInProgress(false);
        },
        [fetchApps, history, appCreationInProgress],
    );

    if (!user) {
        const passportUrl = __appConfig.passport + '?retpath=' + encodeURIComponent(location.href);

        window.location.href = passportUrl;

        return (
            <div className="AppLoaderContainer">
                <Loading centered />
            </div>
        );
    }

    return !snapshot || (showAppStoreUi() && !appStoreSnapshot) ? (
        <div className="AppLoaderContainer">
            <Loading centered />
        </div>
    ) : (
        <SnapshotContext.Provider value={{ snapshot, refreshSnapshot: fetchSkills }}>
            <AppStoreSnapshotContext.Provider
                value={{
                    appStoreSnapshot: appStoreSnapshot ?? {
                        installableApps: [],
                        maxAppsPerUser: 0,
                        targetPlatformsByChannel: {},
                        categories: [],
                        ageRestrictions: [],
                    },
                    refreshAppStoreSnapshot: fetchApps,
                }}
            >
                <Switch>
                    <Route path={routes.app.moderation.test()} exact component={ModerationTestPage} />
                    <Route path={routes.app.settings()} component={SettingsPage} />
                    <Route path={routes.app.skills} exact>
                        {() =>
                            isIframe() ? null : (
                                <SkillsIndex
                                    onCreateSkillClick={() =>
                                        __appConfig.isYangoRequest
                                            ? handleSkillCreationPopupSelect(Channel.SmartHome)
                                            : setCreatePopup({ visible: true })
                                    }
                                />
                            )
                        }
                    </Route>
                    <Route path={routes.app.installableApps(Channel.TvApp)} exact>
                        {showAppStoreUi() ? (
                            <InstallableAppsIndex onCreateAppClick={handleAppCreation} channel={Channel.TvApp} />
                        ) : (
                            <Redirect to={routes.app.root} />
                        )}
                    </Route>
                    <Route path={routes.app.installableApps(Channel.AutoApp)} exact>
                        {showAutoAppUi() ? (
                            <InstallableAppsIndex onCreateAppClick={handleAppCreation} channel={Channel.AutoApp} />
                        ) : (
                            <Redirect to={routes.app.root} />
                        )}
                    </Route>
                    <Route path={routes.app.installableApps(Channel.SmartScreenApp)} exact>
                        {showSmartScreenUi() ? (
                            <InstallableAppsIndex
                                onCreateAppClick={handleAppCreation}
                                channel={Channel.SmartScreenApp}
                            />
                        ) : (
                            <Redirect to={routes.app.root} />
                        )}
                    </Route>
                    <Route path={routes.app.skill()}>
                        {(props) => <SkillPageContainer skillId={props.match?.params.skillId as string} />}
                    </Route>
                    <Route path={routes.app.installableApp(Channel.TvApp)}>
                        {showAppStoreUi() ? (
                            (props) => (
                                <InstallableAppPageContainer
                                    channel={Channel.TvApp}
                                    skillId={props.match?.params!.skillId as string}
                                />
                            )
                        ) : (
                            <Redirect to={routes.app.root} />
                        )}
                    </Route>
                    <Route path={routes.app.installableApp(Channel.AutoApp)}>
                        {showAutoAppUi() ? (
                            (props) => (
                                <InstallableAppPageContainer
                                    channel={Channel.AutoApp}
                                    skillId={props.match?.params!.skillId as string}
                                />
                            )
                        ) : (
                            <Redirect to={routes.app.root} />
                        )}
                    </Route>
                    <Route path={routes.app.installableApp(Channel.SmartScreenApp)}>
                        {showSmartScreenUi() ? (
                            (props) => (
                                <InstallableAppPageContainer
                                    channel={Channel.SmartScreenApp}
                                    skillId={props.match?.params!.skillId as string}
                                />
                            )
                        ) : (
                            <Redirect to={routes.app.root} />
                        )}
                    </Route>
                    <Route path={routes.app.newSkill()} exact>
                        {(props) => <AsyncProgress action={() => createNewSkill(props.match?.params.channel)} />}
                    </Route>
                    <Route path={routes.app.documentation('')} component={DocumentationPage} />
                    <Redirect to={routes.app.root} />
                </Switch>

                <SkillCreationPopup
                    visible={createPopup.visible}
                    progress={createPopup.progress}
                    onSelect={handleSkillCreationPopupSelect}
                    onClose={() => setCreatePopup({ visible: false })}
                    userAgreementLink={links.userAgreement}
                />
            </AppStoreSnapshotContext.Provider>
        </SnapshotContext.Provider>
    );
};

const RoutedApp: React.FC = () => (
    <BrowserRouter>
        <Lang.Provider value={{ lang: __appConfig.lang }}>
            <App />
        </Lang.Provider>
    </BrowserRouter>
);

export { RoutedApp as App };
